import { Calendar } from "@/components/ui/calendar";
import { CalendarEvent } from "@/components/ui/full-calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ROUTE } from "@/routes/constants";
import {
  addDays,
  addWeeks,
  differenceInCalendarWeeks,
  format,
  isSameDay,
  startOfWeek,
} from "date-fns";
import { formatRange } from "@/lib/format-week-range";
import { enUS } from "date-fns/locale/en-US";
import { zhHK } from "date-fns/locale/zh-HK";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { CalendarPickerIcon } from "@/components/calendar-picker-icon";
import { ReactNode, useState } from "react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";

/* ─── types ─── */
interface OverviewAgendaProps {
  events: CalendarEvent[];
  accent: string;
  arrow: string;
  /** pass true when locale is English */
  isEnglish: boolean;
  /** i18n t() for calendar header label */
  calendarLabel: string;
  /** 日期选择器「確定」按钮文案 (t('component.confirm')) */
  confirmLabel: string;
  /** 日期选择器「清除篩選」按钮文案 (t('selfAssess.clearFilter')) */
  clearLabel: string;
  renderTooltip?: (event: CalendarEvent) => ReactNode;
  /** 透传到根面板：用于让父级 flex 容器把日程表撑到剩余高度 (md+ 下底对齐侧栏底) */
  className?: string;
}

/* ─── helpers ─── */

/** Return the Monday of the week containing `date`. */
function getMondayOf(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/** Build 14 consecutive dates starting from the given Monday. */
function buildTwoWeeks(monday: Date): Date[] {
  return Array.from({ length: 14 }, (_, i) => addDays(monday, i));
}

/* 头部三图标(選擇日期/上兩週/下兩週)触控热区：伪元素不参与布局故图标位置不变；高统一 44，宽取相邻不重叠上限=图标宽+gap10(30/34/38)，满 44 宽须拉大 1920 稿间距 */
const HEADER_HIT_AREA =
  "relative after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-[30px] after:-translate-x-1/2 after:-translate-y-1/2 after:content-[''] lg:after:w-[34px] 2xl:after:w-[38px]";

/* ─── 事件 pill 配色（装饰性，按事件 id 稳定取色，呼应设计图的多彩 pill）─── */
const PILL_PALETTE: { body: string; cap: string }[] = [
  { body: "rgba(91,201,232,0.22)", cap: "#5BC9E8" }, // 蓝
  { body: "rgba(255,213,123,0.30)", cap: "#FFD57B" }, // 金
  { body: "rgba(255,158,123,0.28)", cap: "#FF9E7B" }, // 橙
  { body: "rgba(124,229,120,0.30)", cap: "#7FE578" }, // 绿
  { body: "rgba(225,80,238,0.16)", cap: "#E150EE" }, // 紫
  { body: "rgba(180,180,180,0.40)", cap: "#C4C4C4" }, // 灰
];

function pillColor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PILL_PALETTE[h % PILL_PALETTE.length];
}

/* ─── Event pill ─── */
function EventChip({
  event,
  renderTooltip,
}: {
  event: CalendarEvent;
  renderTooltip?: (event: CalendarEvent) => ReactNode;
}) {
  const c = pillColor(event.id);
  const inner = (
    <div
      title={event.title}
      className="relative flex h-6 w-full items-center overflow-hidden rounded-[25px] lg:h-7 2xl:h-[30px]"
      style={{ backgroundColor: c.body }}
    >
      {/* 左侧实心 cap（~28%），呈深色色块 */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[28%] rounded-l-[25px]"
        style={{ backgroundColor: c.cap }}
      />
      <span className="relative truncate pl-[32%] pr-2.5 text-[12px] font-medium leading-none text-[#1A1C1D] lg:text-[13px] xl:text-[14px] 2xl:text-[15px]">
        {event.title}
      </span>
    </div>
  );

  // 有 renderTooltip 且有数据 → 单击 pill 弹 Popover（不直接跳，跳转走浮层内链接）
  if (renderTooltip && event.data) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left"
          >
            {inner}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="center"
          className="w-auto max-w-[260px] rounded-2xl border-none p-4 shadow-lg"
        >
          {renderTooltip(event)}
        </PopoverContent>
      </Popover>
    );
  }

  if (event.data) {
    return (
      <NavLink
        to={ROUTE.STUDENT.HOMEWORK}
        state={[event.data]}
        className="block w-full"
        tabIndex={0}
      >
        {inner}
      </NavLink>
    );
  }
  return <div className="w-full">{inner}</div>;
}

/* ─── 单行（7 列日期 + pill）；星期头单独渲染，故此处不含星期 ─── */
function WeekRow({
  week,
  weekEvents,
  renderTooltip,
  selectedDate,
}: {
  week: Date[];
  weekEvents: CalendarEvent[][];
  renderTooltip?: (event: CalendarEvent) => ReactNode;
  /** 日期选择器跳转后高亮的所选日（无则不高亮） */
  selectedDate?: Date;
}) {
  return (
    <div className="flex gap-2 lg:gap-3 [@media(min-height:1200px)]:min-h-0 [@media(min-height:1200px)]:flex-1 [@media(max-height:1000px)]:min-h-0 [@media(max-height:1000px)]:flex-1 [@media(min-height:1200px)_and_(max-width:1279px)]:flex-none!">
      {week.map((day, i) => {
        const isToday = isSameDay(day, new Date());
        const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
        return (
          <div
            key={day.toISOString()}
            className="flex min-w-0 flex-1 flex-col items-center gap-2 2xl:gap-2.5 [@media(min-height:1200px)]:min-h-0 [@media(max-height:1000px)]:min-h-0 [@media(max-height:1000px)]:gap-1!"
          >
            {/* 日期数字 — 今天/所选日为绿字（主题强调色），其余灰；所选日额外加软底色 chip
                （绝对定位浮层，不影响行高布局） */}
            <span
              className="relative inline-flex items-center justify-center text-base font-normal leading-none tabular-nums lg:text-lg xl:text-xl 2xl:text-2xl [@media(max-height:1000px)]:text-base!"
              style={{ color: isToday || isSelected ? "var(--sidebar-active)" : "#94A3B8" }}
            >
              {isSelected && (
                <span
                  aria-hidden
                  className="absolute -inset-x-2 -inset-y-1 rounded-full"
                  style={{ backgroundColor: "color-mix(in srgb, var(--sidebar-active) 18%, transparent)" }}
                />
              )}
              <span className="relative">{format(day, "d")}</span>
            </span>
            <div
              className="flex h-[54px] w-full flex-col gap-1.5 overflow-y-auto pr-0.5 lg:h-[62px] 2xl:h-[66px] [@media(min-height:1200px)]:h-auto! [@media(min-height:1200px)]:min-h-[66px] [@media(min-height:1200px)]:flex-1 [@media(max-height:1000px)]:h-auto! [@media(max-height:1000px)]:min-h-0 [@media(max-height:1000px)]:flex-1 [scrollbar-color:rgba(0,0,0,0.2)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1 [@media(min-height:1200px)_and_(max-width:1279px)]:h-[62px]! [@media(min-height:1200px)_and_(max-width:1279px)]:min-h-0! [@media(min-height:1200px)_and_(max-width:1279px)]:flex-none!"
            >
              {weekEvents[i].map((ev) => (
                <EventChip key={ev.id} event={ev} renderTooltip={renderTooltip} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main component ─── */
export default function OverviewAgenda({
  events,
  isEnglish,
  calendarLabel,
  confirmLabel,
  clearLabel,
  renderTooltip,
  className,
}: OverviewAgendaProps) {
  const { t } = useTranslation();
  // weekOffset: 0 = current bi-week, +1 = next, -1 = previous, etc.
  const [weekOffset, setWeekOffset] = useState(0);
  // 日期选择器：弹窗开合、弹窗内待确认日期(draft)、确认后高亮的所选日
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<Date | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // Compute the Monday of the first displayed week
  const baseMonday = addWeeks(getMondayOf(new Date()), weekOffset * 2);
  const days = buildTwoWeeks(baseMonday);

  // Group events by day index (0-13)
  const eventsByDay: CalendarEvent[][] = days.map((day) =>
    events.filter((ev) => isSameDay(ev.start, day))
  );

  const locale = isEnglish ? enUS : zhHK;

  const handlePrev = () => setWeekOffset((o) => o - 1);
  const handleNext = () => setWeekOffset((o) => o + 1);

  // 「確定」：把所选日所在的「两周桶」算成 weekOffset 跳过去，并高亮该日
  // base = 本周一 + offset*2 周；offset = floor(所选周一相对本周一的周差 / 2)，floor 对过去日(负周差)亦正确
  const handleConfirm = () => {
    if (draftDate) {
      const weeksDiff = differenceInCalendarWeeks(
        getMondayOf(draftDate),
        getMondayOf(new Date()),
        { weekStartsOn: 1 }
      );
      setWeekOffset(Math.floor(weeksDiff / 2));
      setSelectedDate(draftDate);
    }
    setPickerOpen(false);
  };

  // 「清除篩選」：回到本周两周、清掉高亮（复用原「回到本周」语义）
  const handleClearFilter = () => {
    setWeekOffset(0);
    setSelectedDate(undefined);
    setDraftDate(undefined);
    setPickerOpen(false);
  };

  // Split 14 days into two rows of 7
  const week1 = days.slice(0, 7);
  const week2 = days.slice(7, 14);
  const events1 = eventsByDay.slice(0, 7);
  const events2 = eventsByDay.slice(7, 14);

  // 矮屏(平板/笔电 max-h:1000)圆角随课业 section 一起收到 28px，两块右侧面板圆角一致
  // (Figma 平板设计两面板同圆角)；rounded-[50px] 仍为 ≥1000 高屏/1920 基线，不变。
  // 矮屏标题内距：顶/左 24(pt-6/px-6) 还原 1920 留白感，底仍 16(pb-4) 让日历贴底、面板底缘对齐侧栏。
  return (
    <div className={cn("rounded-[50px] bg-[#F3F3F4] p-6 lg:p-8 2xl:p-10 [@media(min-height:1200px)]:flex [@media(min-height:1200px)]:flex-col [@media(max-height:1000px)]:flex [@media(max-height:1000px)]:flex-col [@media(max-height:1000px)]:rounded-[28px]! [@media(max-height:1000px)]:px-6! [@media(max-height:1000px)]:pt-6! [@media(max-height:1000px)]:pb-4! [@media(min-height:1200px)_and_(max-width:1279px)]:block!", className)}>
      {/* Header：标题左、图标右（黑色，无填充底） */}
      <div className="mb-5 flex items-center gap-2.5 2xl:mb-7 [@media(max-height:1000px)]:mb-3!">
        <CalendarDays className="size-5 shrink-0 text-[#1A1C1D] lg:size-6 2xl:size-7" />
        <h2 className="min-w-0 truncate text-xl font-bold text-[#1A1C1D] xl:text-[22px] 2xl:text-2xl">
          {calendarLabel}
        </h2>
        {/* ZH-06 当前两周完整日期范围：矮屏(max-h:1000)原本只有 MM/dd 无年份，改为与正常档同走 formatRange(只把字号压回 text-sm) */}
        <span className="shrink-0 text-sm font-normal tabular-nums text-[#64748B] xl:text-base [@media(max-height:1000px)]:text-sm!">
          {formatRange(days[0], days[13], isEnglish, locale)}
        </span>

        {/* spacer */}
        <span className="flex-1" />

        {/* 日历图标 → 弹月历选择器：选日 + 確定 跳到该日所在两周并高亮；清除篩選 回本周 */}
        <Popover
          open={pickerOpen}
          onOpenChange={(o) => {
            setPickerOpen(o);
            if (o) setDraftDate(selectedDate ?? new Date());
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t('overview.pickDate', { defaultValue: '選擇日期' })}
              title={t('overview.pickDate', { defaultValue: '選擇日期' })}
              className={cn("text-[#1A1C1D] transition hover:opacity-60", HEADER_HIT_AREA)}
            >
              {/* 尺寸与标题左侧的 CalendarDays 保持同一档：size-5 / lg:size-6 / 2xl:size-7 */}
              <CalendarPickerIcon className="size-5 lg:size-6 2xl:size-7" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-auto rounded-2xl border-none p-3 shadow-lg"
          >
            <Calendar
              mode="single"
              selected={draftDate}
              onSelect={setDraftDate}
              defaultMonth={draftDate ?? new Date()}
              locale={locale}
              showOutsideDays
              className="p-0"
            />
            <div className="mt-1 flex items-center justify-between gap-3 border-t border-black/5 pt-3">
              <button
                type="button"
                onClick={handleClearFilter}
                className="rounded-full px-4 py-1.5 text-sm font-medium text-[#64748B] transition hover:bg-black/5"
              >
                {clearLabel}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="rounded-full px-5 py-1.5 text-sm font-semibold text-white transition hover:opacity-90"
                style={{ backgroundColor: "var(--sidebar-active)" }}
              >
                {confirmLabel}
              </button>
            </div>
          </PopoverContent>
        </Popover>
        {/* 上两周 */}
        <button
          type="button"
          onClick={handlePrev}
          aria-label={t('overview.prevTwoWeeks', { defaultValue: '上兩週' })}
          title={t('overview.prevTwoWeeks', { defaultValue: '上兩週' })}
          className={cn("text-[#1A1C1D] transition hover:opacity-60", HEADER_HIT_AREA)}
        >
          {/* 与标题图标、選擇日期同档：size-5 / lg:size-6 / 2xl:size-7（<1024 时三者不再差 4px） */}
          <ChevronLeft className="size-5 lg:size-6 2xl:size-7" strokeWidth={2.5} />
        </button>
        {/* 下两周 */}
        <button
          type="button"
          onClick={handleNext}
          aria-label={t('overview.nextTwoWeeks', { defaultValue: '下兩週' })}
          title={t('overview.nextTwoWeeks', { defaultValue: '下兩週' })}
          className={cn("text-[#1A1C1D] transition hover:opacity-60", HEADER_HIT_AREA)}
        >
          <ChevronRight className="size-5 lg:size-6 2xl:size-7" strokeWidth={2.5} />
        </button>
      </div>

      {/* 星期头（Mon–Sun）— 仅一行，两行日期共用 */}
      <div className="mb-3 flex gap-2 lg:gap-3 2xl:mb-4 [@media(max-height:1000px)]:mb-2!">
        {week1.map((day) => (
          <div key={"wd-" + day.toISOString()} className="min-w-0 flex-1 text-center">
            <span className="text-sm font-normal uppercase tracking-wide text-[#94A3B8] lg:text-base xl:text-lg 2xl:text-2xl [@media(max-height:1000px)]:text-sm!">
              {format(day, "EEE", { locale })}
            </span>
          </div>
        ))}
      </div>

      {/* 两行日期；>1200 撑满高度；<1000(矮屏笔记本/平板)两行 flex 均分剩余高、格子可缩以容下两周不溢出(1920×1080 均不触发) */}
      <div className="flex flex-col gap-4 2xl:gap-6 [@media(min-height:1200px)]:min-h-0 [@media(min-height:1200px)]:flex-1 [@media(max-height:1000px)]:flex-1 [@media(max-height:1000px)]:min-h-0 [@media(max-height:1000px)]:gap-2! [@media(min-height:1200px)_and_(max-width:1279px)]:flex-none!">
        <WeekRow week={week1} weekEvents={events1} renderTooltip={renderTooltip} selectedDate={selectedDate} />
        <WeekRow week={week2} weekEvents={events2} renderTooltip={renderTooltip} selectedDate={selectedDate} />
      </div>
      {events.length === 0 && (
        <p className="mt-3 text-center text-sm text-[#94A3B8]">
          {t('common.no-content', { defaultValue: '無內容顯示' })}
        </p>
      )}
    </div>
  );
}
